import { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CONVERSATIONS } from "@/constants/testIds";

const ICON_BUTTON = "rounded-full p-1.5 [@media(pointer:coarse)]:p-3 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50";

// Fix AO (2026-10-23): pulgar arriba / abajo en las respuestas del agente (y "usar como ejemplo" en las del equipo).
// Lo que se califica se usa para entrenar al agente (Agente IA > Entrenamiento).
export default function MessageFeedback({ message }) {
  const [feedback, setFeedback] = useState(message.feedback || null);
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [correction, setCorrection] = useState("");
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  useEffect(() => { setFeedback(message.feedback || null); }, [message.id, message.feedback]);

  const body = (message.body || "").trim();
  const isAgent = message.sender_type === "agent";
  const rateable = message.direction === "outbound" && (isAgent || message.sender_type === "staff") && !message.template && !(message.media && message.media.url) && body && !body.startsWith("[");
  if (!rateable) return null;

  const fail = (title, err) => toast({ title, description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });

  const send = async (payload, okTitle) => {
    setBusy(true);
    try {
      const { data } = await api.post("/agent/training/feedback", { message_id: message.id, ...payload });
      setFeedback({ rating: data.rating, comment: data.comment, correction: data.correction });
      if (okTitle) toast({ title: okTitle });
      return true;
    } catch (err) {
      fail("No se pudo guardar la calificación", err);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.delete(`/agent/training/feedback/message/${message.id}`);
      setFeedback(null);
      return true;
    } catch (err) {
      fail("No se pudo quitar la calificación", err);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const clickUp = () => (feedback?.rating === "up" ? clear() : send({ rating: "up" }, isAgent ? "Guardada como buena respuesta" : "Guardada como ejemplo para el agente"));
  const clickDown = () => {
    setComment(feedback?.rating === "down" ? feedback.comment || "" : "");
    setCorrection(feedback?.rating === "down" ? feedback.correction || "" : "");
    setOpen(true);
  };
  const saveDown = async () => {
    if (await send({ rating: "down", comment: comment.trim() || null, correction: correction.trim() || null }, "Corrección guardada")) setOpen(false);
  };
  const removeDown = async () => {
    if (await clear()) setOpen(false);
  };

  const up = feedback?.rating === "up";
  const down = feedback?.rating === "down";
  return (
    <>
      <div className="flex items-center gap-0.5 mt-0.5 mx-1" data-testid={`${CONVERSATIONS.feedbackRowPrefix}${message.id}`}>
        <button type="button" disabled={busy} onClick={clickUp} className={`${ICON_BUTTON} ${up ? "text-[#5B7A54]" : ""}`} aria-pressed={up}
          aria-label={isAgent ? "Buena respuesta" : "Usar como ejemplo para el agente"} title={isAgent ? "Buena respuesta" : "Usar como ejemplo para el agente"} data-testid={`${CONVERSATIONS.feedbackUpPrefix}${message.id}`}>
          <ThumbsUp className={`w-3.5 h-3.5 ${up ? "fill-current" : ""}`} />
        </button>
        {isAgent && (
          <button type="button" disabled={busy} onClick={clickDown} className={`${ICON_BUTTON} ${down ? "text-[#A4473C]" : ""}`} aria-pressed={down}
            aria-label="Respuesta a mejorar" title="Respuesta a mejorar" data-testid={`${CONVERSATIONS.feedbackDownPrefix}${message.id}`}>
            <ThumbsDown className={`w-3.5 h-3.5 ${down ? "fill-current" : ""}`} />
          </button>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid={CONVERSATIONS.feedbackDialog}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">¿Qué estuvo mal?</DialogTitle>
            <DialogDescription>Cuando el agente reciba una consulta parecida, va a tener en cuenta esta corrección.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm whitespace-pre-wrap bg-muted rounded-lg px-3 py-2 max-h-32 overflow-y-auto break-words">{body}</p>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Qué estuvo mal</p>
              <Textarea value={comment} maxLength={500} rows={2} onChange={(e) => setComment(e.target.value)} placeholder="Por ejemplo: pregunta el nombre demasiado pronto" data-testid={CONVERSATIONS.feedbackCommentInput} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Cómo debió responder (opcional)</p>
              <Textarea value={correction} maxLength={700} rows={3} onChange={(e) => setCorrection(e.target.value)} data-testid={CONVERSATIONS.feedbackCorrectionInput} />
            </div>
            {!comment.trim() && !correction.trim() && <p className="text-xs text-muted-foreground">Sin un motivo o una respuesta mejor, la calificación queda registrada pero no cambia lo que responde el agente.</p>}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            {feedback?.rating === "down" && <Button variant="ghost" onClick={removeDown} disabled={busy} className="sm:mr-auto" data-testid={CONVERSATIONS.feedbackRemoveButton}>Quitar calificación</Button>}
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={saveDown} disabled={busy} className="bg-brand hover:bg-brand-hover text-white" data-testid={CONVERSATIONS.feedbackSaveButton}>{busy ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
